import React, { useState, useMemo } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import MiniProductReviewsBlock from './MiniProductReviewsBlock';
import { Link, useNavigate } from 'react-router-dom';
import BsImages from '../icons/BsImages';
import { any, bool, number, oneOfType, string } from 'prop-types';
import ResponsiveImage from './ResponsiveImage';
import AppContext from './context/AppContext';
import { useContext } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import ProductQuickView from './ProductQuickView';
import { CartContext } from './context/CartContext';
import { ToastContext } from './context/ToastContext';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

/**
    CONFIGURATION
*/
const SHOW_DELAY = 3000;
const HIDE_DELAY = 5 * 60 * 1000;

export default function SearchPageProductListItem({ isMobile, title, handle, imageUrl, price, compareAtPrice, availableForSale, searchLoading, avgRating, totalReviews, collectionHandle, isFeatured, inventoryLevelId, availableQuantity, bypassNegativeQuantity, view }) {
    const { appContext } = useContext(AppContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const productBasePath = productPath[0].pathBeforeParams;
    const [toggle, setToggle] = useState(false);
    const appConfig = appContext.config.themeConfig;
    const navigate = useNavigate();
    collectionHandle = encodeURIComponent(collectionHandle.trim());

    const handleToggle = (toggle) => {
        setToggle(toggle);
    };

    const renderProductQuickView = (props) => (
        <ProductQuickView
            OverlayTriggerProps={props}
            handle={handle}
            handleToggle={handleToggle}
        />
    );

    const {
        cartState,
        cartUpdating,
        addLineItem,
    } = useContext(CartContext);
    const { sendMessage } = useContext(ToastContext);

    const addedQuantity = useMemo(() => {
        if (!cartState) {
            return 0;
        }

        return cartState.filter((i) => i.inventoryLevelId === inventoryLevelId).at(0)?.quantity ?? 0;
    }, [cartState, inventoryLevelId]);

    const quantity = useMemo(() => {
        return parseInt((bypassNegativeQuantity ? Infinity : availableQuantity) - addedQuantity);
    }, [bypassNegativeQuantity, availableQuantity, addedQuantity]);

    const onAddToCartClick = async (e) => {
        e.stopPropagation();

        try {

            const result = await addLineItem(inventoryLevelId, 1);
            if (result.success) {
                sendMessage('Product added to cart', 'success');
            } else {
                sendMessage(result?.error || 'Failed to add item to cart', 'danger');
            }
        } catch (e) {
            sendMessage(e.message, 'error');
        }
    };

    return (<li
        className={`search-page--product-list-item${searchLoading === true ? ' loading' : ''} ${view}`}
        onClick={() => {
            let url = '';
            if (!searchLoading) {
                if (typeof collectionHandle === 'string' && collectionHandle !== '') {
                    url += `${collectionPath[0].pathBeforeParams}/${collectionHandle}`;
                }
                url += `${productBasePath}/${handle}`;
                navigate(url);
            }
        }}>
        {isFeatured === true && (<span
            className={'search-page-list-item--featured'}>Featured</span>)}
        <figure
            className={'search-page--product-list-item-image'}
            data-product-item-image>
            <OverlayTrigger
                delay={{
                    show: SHOW_DELAY,
                    hide: HIDE_DELAY,
                }}
                show={toggle}
                onToggle={handleToggle}
                overlay={renderProductQuickView}
            >
                <div
                    className={'product-quick-view-overlay-trigger'}></div>
            </OverlayTrigger>
            {imageUrl && (<ResponsiveImage
                imageUrl={imageUrl}
                imageAlt={title || ''}
                mobileSize={'200px'}
                desktopSize={'200px'}></ResponsiveImage>)}
            {!imageUrl && (
                <BsImages
                    style={{ maxWidth: '150px' }} />)}
            {!searchLoading && (<>
                {!availableForSale && (<span
                    className={'search-page-list-item--badge sold-out'}>Sold out</span>)}
                {(availableForSale === 1 && !isNaN(compareAtPrice) && compareAtPrice > price) && (<span
                    className={'search-page-list-item--badge compare-price'}>Save {getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice - price).toFixed(2)} {appConfig.storeCurrency}</span>)}
            </>)}
        </figure>
        <div
            className={'search-page--product-list-item-description'}>
            {availableForSale === 1 && !isNaN(compareAtPrice) && compareAtPrice > price && (<span
                className={'search-page-list-item--compare-price'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice).toFixed(2)} {appConfig.storeCurrency}</span>)}
            <span
                className={'search-page--product-list-item-price'}>
                {(!isNaN(price) && price > 0) && (<>
                    {getSymbolFromCurrency(appConfig.storeCurrency)}{price.toFixed(2)} {appConfig.storeCurrency}
                </>)}
                {price === 0 && (<>
                    {getSymbolFromCurrency(appConfig.storeCurrency)}0.0 {appConfig.storeCurrency}
                </>)}
            </span>
            <h2
                className={'search-page--product-list-item-title'}><Link
                    to={!searchLoading ? `${productBasePath}/${handle}` : '#'}>{title ?? ''}</Link></h2>
            <MiniProductReviewsBlock
                avgRating={avgRating}
                totalReviews={totalReviews}
                loading={searchLoading} />
        </div>
        <button
            className={'cart-add--btn search-page--product-list-item-button'}
            onClick={onAddToCartClick}
            disabled={(cartUpdating || !availableForSale || quantity <= 0)}
            style={{
                ...(cartUpdating || !availableForSale || quantity <= 0) && ({
                    backgroundColor: 'lightgray',
                    borderColor: 'lightgray'
                })
            }}
        >
            Add to Cart
        </button>
    </li>);
}

SearchPageProductListItem.propTypes = {
    isMobile: bool,
    title: string,
    handle: string,
    imageUrl: any,
    price: oneOfType([number, string]),
    compareAtPrice: oneOfType([number, string]),
    availableForSale: oneOfType([number, bool]),
    searchLoading: bool,
    avgRating: oneOfType([number, bool]),
    totalReviews: oneOfType([number, bool]),
    collectionHandle: string,
    isFeatured: bool,
    inventoryLevelId: number,
    availableQuantity: number,
    bypassNegativeQuantity: bool,
    view: string,
};

SearchPageProductListItem.defaultProps = {
    avgRating: 0,
    totalReviews: 0,
    collectionHandle: '',
    isFeatured: false,
};